import type { Environment, EnvironmentRef, User } from '@fleequid-secrets/shared';
import { createContext, useContext, useEffect, useRef } from 'react';
import { ApiError } from '@/api/client';
import type { IconName } from '@/components/Icon';

// What every page shares with the shell around it: who is signed in, the environments, the toast, the palette, and
// the confirmation a protected environment asks for.

export interface Command {
    icon: IconName;
    label: string;
    sub?: string;
    key?: string;
    run: () => void;
}

export interface ToastAction {
    label: string;
    run: () => void;
}

export interface Shell {
    user: User;
    environments: Environment[];
    // The environment on screen in VARIABLES, or the last one seen there.
    current: Environment | null;
    say: (message: string, action?: ToastAction) => void;
    // The slug to send once the person confirmed a change in a protected environment (null when it is not one);
    // rejects with Cancelled when they decline.
    confirmChange: (environment: EnvironmentRef, what: string) => Promise<string | null>;
    // A page's own commands for the palette: a getter, read when the palette opens.
    setPageCommands: (commands: () => Command[]) => void;
    openPalette: () => void;
}

export const ShellContext = createContext<Shell | null>(null);

export function useShell(): Shell {
    const shell = useContext(ShellContext);
    if (!shell) {
        throw new Error('useShell outside the shell');
    }

    return shell;
}

// The page's commands, current at every render; the palette asks for them when it opens.
export function usePageCommands(commands: Command[]): void {
    const { setPageCommands } = useShell();
    const latest = useRef(commands);
    useEffect(() => {
        latest.current = commands;
    });
    useEffect(() => {
        setPageCommands(() => latest.current);

        return () => setPageCommands(() => []);
    }, [setPageCommands]);
}

export class Cancelled extends Error {
    override readonly name = 'Cancelled';
}

// A change that a protected environment may ask to confirm: asked first when the environment is known to be
// protected, and asked then retried when the server says it became so meanwhile.
export async function guarded<T>(
    shell: Pick<Shell, 'confirmChange'>,
    environment: EnvironmentRef,
    what: string,
    run: (confirm: string | null) => Promise<T>,
): Promise<T> {
    const confirm = await shell.confirmChange(environment, what);
    try {
        return await run(confirm);
    } catch (error) {
        if (error instanceof ApiError && error.code === 'confirm' && !confirm) {
            return run(await shell.confirmChange({ ...environment, protected: true }, what));
        }
        throw error;
    }
}

// G, then a module's letter, is the shell's: a page leaves alone the letter that follows a G.
let goAt = 0;
export const markGo = (): void => {
    goAt = Date.now();
};
export const clearGo = (): void => {
    goAt = 0;
};
export const goPending = (): boolean => Date.now() - goAt < 1200;
